import {
  CreativeDocumentMetadata,
  CreativeDocumentModel,
  FrameRange,
  Interval,
} from 'content-creation-common/models';

import {
  convertObjectToMap,
  convertMapToObject,
  isEmpty as isEmptyObject,
  isEmptySet,
  isNil,
  isNotNil,
} from '../../../util/object';
import { isEmpty as isEmptyArray, sortBy } from '../../../util/array';
import {
  getAllElements,
  getLayerById,
  getLayerDescendantsById,
} from '../../../selectors/element';
import { SHOT_ELEMENT_TYPES } from '../constants';
import { DEFAULT_MASK_NAME } from '../../creative/constants';

// Not using a selector here due to dependencies and being in a worker.
function getShotGroupsSortedByStartFrame(elements) {
  if (isNil(elements)) return [];
  const unsortedShotGroupElements = elements.filter(
    ({ meta }) => isNotNil(meta) && meta.shotType === SHOT_ELEMENT_TYPES.GROUP,
  );
  return sortBy(unsortedShotGroupElements, 'frameStart');
}

export function getAdjustedElementTransformKeyFrames(
  transforms,
  numSkippedFrames,
) {
  if (isNil(transforms)) return transforms;
  // TODO: make use of Models in restoreSkippedShot so we won't have to check if element.transforms is a map or not
  return transforms instanceof Map
    ? getAdjustedTransformsMap(transforms, numSkippedFrames)
    : getAdjustedTransformsObject(transforms, numSkippedFrames);
}

function getAdjustedTransformsMap(transforms, numSkippedFrames) {
  const transformKeyFrames = [...transforms.keys()].map((val) =>
    parseInt(val, 10),
  );
  // If there are less than two transformations, it is in default state and does not need to be adjusted
  if (transformKeyFrames.length < 2) return transforms;
  const adjustedTransforms = new Map();
  for (let i = 0; i < transformKeyFrames.length; i++) {
    const keyFrame = transformKeyFrames[i];
    const adjustedKeyFrame = keyFrame + numSkippedFrames;
    adjustedTransforms.set(
      adjustedKeyFrame.toString(),
      transforms.get(keyFrame.toString()),
    );
  }
  return adjustedTransforms;
}

function getAdjustedTransformsObject(transforms, numSkippedFrames) {
  const transformsMap = convertObjectToMap(transforms);
  const adjustedTransformsMap = getAdjustedTransformsMap(
    transformsMap,
    numSkippedFrames,
  );
  return convertMapToObject(adjustedTransformsMap);
}

/**
 * Takes in a CreativeDocumentModel for an episode and updates it so that any skipped shots are removed.
 * Skipped shot data and output range data is moved to documentModel.metadata.skippedShotData.
 * @param {CreativeDocumentModel} documentModel
 */
// Example processing of episode data
//
// Input shot list and output ranges
// shots: [{a:0-10}, {b:11-20/skipped}, {c:21-30/skipped}, {d:31-40}, {e:41-45}, {f:46-50/skipped}, {g:51-60}]
// outputRanges: [{ab:0-20}, {cd:21-40}, {efg:41-60}]
// rootMask: [0 - 60]

// Output shot list and output ranges
// shots: [{a:0-10}, {d:11-20}, {e:21-25}, {g:26-35}]
// outputRanges: [{ab:0-10}, {cd:11-20}, {efg:21-35}]
// skippedShotData: [{b:11-20/skipped}, {c:21-30/skipped}, {f:46-50/skipped}]
// skippedShotData.outputRanges: [{ab:0-20}, {cd:21-40}, {efg:41-60}]
// rootMask: [0 - 35]

export function extractSkippedShots(documentModel) {
  const skippedContent = new Map();
  const skippedElements = new Map();
  const skippedMedia = new Map();

  const { content, elements, media, metadata, rootElementIds } = documentModel;

  if (elements.size === 0) return documentModel;

  const sortedShotGroupElements = getShotGroupsSortedByStartFrame([
    ...elements.values(),
  ]);

  // skip this process if creative doesn't have skipped shots
  const hasSkippedShots = sortedShotGroupElements.some((shotGroupElement) => {
    const { meta } = shotGroupElement;
    return !isEmptyObject(meta) && Boolean(meta.shotSkipped);
  });
  if (!hasSkippedShots) return documentModel;

  const { outputRanges: metadataOutputRanges } = metadata;
  const creativeFrameRange = CreativeDocumentModel.getFrameRangeForRootMask(
    documentModel,
  );
  const hasOutputRanges = !isEmptySet(metadataOutputRanges);
  const outputRanges = hasOutputRanges
    ? metadataOutputRanges
    : new Set([creativeFrameRange]);

  const sortedOutputRanges = sortBy([...outputRanges], 'start');

  const skippedElementIds = new Set();
  let numSkippedFrames = 0;
  let endFrameOfLastUnskippedShot = 0;

  let currentOutputRangeIndex = 0;
  let outputRangeStart = 0;

  const condensedOutputRanges = new Set();
  sortedShotGroupElements.forEach((shotGroupElement) => {
    const { elementId, frameEnd, frameStart, meta } = shotGroupElement;

    const { shotSkipped } = meta;

    // Keep track of which output range we're in
    // If the frame is beyond the current output range
    if (frameStart > sortedOutputRanges[currentOutputRangeIndex].end) {
      // if endFrameOfLastUnskippedShot didn't advance, a whole range was skipped.
      const isEntireRangeSkipped =
        outputRangeStart === endFrameOfLastUnskippedShot + 1;
      if (!isEntireRangeSkipped) {
        // complete the current condensed output range
        condensedOutputRanges.add(
          new Interval(outputRangeStart, endFrameOfLastUnskippedShot),
        );
        outputRangeStart = endFrameOfLastUnskippedShot + 1;
      }
      // advance to the next output range
      currentOutputRangeIndex += 1;
    }

    // TODO(omegdadi): Don't use state selectors to get layers, take documentModel.
    const stateJSON = documentModel.toJSON();
    const currentLayer = getLayerById(stateJSON, elementId);
    const descendantLayers = getLayerDescendantsById(stateJSON, elementId);
    const layersForShot = [currentLayer, ...descendantLayers];

    if (shotSkipped) {
      // keep track of how many frames have been skipped
      numSkippedFrames += frameEnd - frameStart + 1;
      layersForShot.forEach((layer) => {
        const {
          contentId: layerContentId,
          elementId: layerElementId,
          mediumId: layerMediaId,
        } = layer;
        skippedContent.set(layerContentId, content.get(layerContentId));
        content.delete(layerContentId);

        skippedElements.set(layerElementId, elements.get(layerElementId));
        elements.delete(layerElementId);

        if (rootElementIds.has(layerElementId)) {
          skippedElementIds.add(layerElementId);
          rootElementIds.delete(layerElementId);
        }

        if (isNotNil(layerMediaId)) {
          skippedMedia.set(layerMediaId, media.get(layerMediaId));
          media.delete(layerMediaId);
        }
      });
      return;
    }

    layersForShot.forEach((layer) => {
      const currentElement = elements.get(layer.elementId);
      currentElement.frameEnd -= numSkippedFrames;
      currentElement.frameStart -= numSkippedFrames;
      currentElement.transforms = getAdjustedElementTransformKeyFrames(
        currentElement.transforms,
        -numSkippedFrames,
      );
      endFrameOfLastUnskippedShot = currentElement.frameEnd;
    });
  });

  if (endFrameOfLastUnskippedShot >= outputRangeStart) {
    condensedOutputRanges.add(
      new Interval(outputRangeStart, endFrameOfLastUnskippedShot),
    );
  }

  const { masks } = documentModel;
  const existingRootMask = masks.get(DEFAULT_MASK_NAME);
  existingRootMask.frameEnd = endFrameOfLastUnskippedShot;

  const skippedShotData = new CreativeDocumentModel();
  skippedShotData.content = skippedContent;
  skippedShotData.elements = skippedElements;
  skippedShotData.media = skippedMedia;
  skippedShotData.rootElementIds = skippedElementIds;

  const skippedShotMetadata = new CreativeDocumentMetadata();
  skippedShotData.metadata = skippedShotMetadata;
  metadata.skippedShotData = skippedShotData;

  // if creative did not have output ranges, don't set it
  if (hasOutputRanges) {
    metadata.outputRanges = condensedOutputRanges;
    skippedShotMetadata.outputRanges = outputRanges;
  }

  return documentModel;
}

/**
 * Takes in a creative state object for an episode and updates it so that any skipped shots are replaced.
 * Skipped shot data and output range data is moved from state.creative.metadata.skippedShotData.
 * @param {ReduxState} state
 */
// Example processing of episode data
//
// Input shot list and output ranges
// shots: [{a:0-10}, {d:11-20}, {e:21-25}, {g:26-35}]
// outputRanges: [{ab:0-10}, {cd:11-20}, {efg:21-35}]
// skippedShotData: [{b:11-20/skipped}, {c:21-30/skipped}, {f:46-50/skipped}]
// skippedShotData.outputRanges: [{ab:0-20}, {cd:21-40}, {efg:41-60}]
// rootMask: [0 - 35]
//
// Output shot list and output ranges
// shots: [{a:0-10}, {b:11-20/skipped}, {c:21-30/skipped}, {d:31-40}, {e:41-45}, {f:46-50/skipped}, {g:51-60}]
// outputRanges: [{ab:0-20}, {cd:21-40}, {efg:41-60}]
// rootMask: [0 - 60]

// TODO(ADS-38409): This is here for now, and will soon use a documentModel
export function restoreSkippedShots(creativeData) {
  const { elements, metadata } = creativeData;
  if (isNil(metadata)) return creativeData;

  const { skippedShotData } = metadata;
  if (isEmptyObject(skippedShotData)) return creativeData;

  const shotGroupElements = getAllElements(creativeData);
  const skippedShotGroupElements = getAllElements(skippedShotData);
  const orderedShotGroupElements = getShotGroupsSortedByStartFrame(
    shotGroupElements,
  );
  const orderedSkippedShotGroupElements = getShotGroupsSortedByStartFrame(
    skippedShotGroupElements,
  );

  let lastShotEndFrame = 0;
  let frameIndex = 0;

  // if all shots are skipped
  if (isEmptyArray(orderedShotGroupElements)) {
    if (!isEmptyArray(orderedSkippedShotGroupElements)) {
      const lastSkippedShot =
        orderedSkippedShotGroupElements[
          orderedSkippedShotGroupElements.length - 1
        ];
      lastShotEndFrame = lastSkippedShot.frameEnd;
    }
  } else {
    let numFramesInserted = 0;
    let nextSkippedShotIndex = 0;
    const { elementsById } = elements;

    orderedShotGroupElements.forEach((shotGroupElement) => {
      // Check if there is a skipped shot on deck
      let nextSkippedShot =
        orderedSkippedShotGroupElements[nextSkippedShotIndex];
      while (
        isNotNil(nextSkippedShot) &&
        nextSkippedShot.frameStart === frameIndex
      ) {
        // if there is, add the shot's length to the total frames inserted count.
        const { frameEnd, frameStart } = nextSkippedShot;

        // We won't add the skipped shot into the list just yet,
        // we'll do that for all skipped shots once at the end.
        const shotLength = new FrameRange(frameStart, frameEnd).length;
        frameIndex += shotLength;
        numFramesInserted += shotLength;
        nextSkippedShotIndex++;
        nextSkippedShot = orderedSkippedShotGroupElements[nextSkippedShotIndex];
      }

      // If there are no skipped shots that need to be inserted,
      // adjust the current shots frames by how many frames have been restored.
      shotGroupElement.frameEnd += numFramesInserted;
      shotGroupElement.frameStart += numFramesInserted;
      shotGroupElement.transforms = getAdjustedElementTransformKeyFrames(
        shotGroupElement.transforms,
        +numFramesInserted,
      );

      // Update all the child layers of the shot group.
      const descendantLayers = getLayerDescendantsById(
        creativeData,
        shotGroupElement.elementId,
      );
      descendantLayers.forEach((layer) => {
        const currentElement = elementsById[layer.elementId];
        currentElement.frameEnd = shotGroupElement.frameEnd;
        currentElement.frameStart = shotGroupElement.frameStart;
        currentElement.transforms = getAdjustedElementTransformKeyFrames(
          currentElement.transforms,
          +numFramesInserted,
        );
      });

      const shotLength = new FrameRange(
        shotGroupElement.frameStart,
        shotGroupElement.frameEnd,
      ).length;
      frameIndex += shotLength;
    });

    let lastSkippedShotEndFrame = 0;
    if (!isEmptyArray(orderedSkippedShotGroupElements)) {
      const lastSkippedShot =
        orderedSkippedShotGroupElements[
          orderedSkippedShotGroupElements.length - 1
        ];
      lastSkippedShotEndFrame = lastSkippedShot.frameEnd;
    }
    lastShotEndFrame = Math.max(frameIndex, lastSkippedShotEndFrame);
  }

  const { content, media } = creativeData;

  const {
    content: skippedContent,
    elements: skippedElements,
    media: skippedMedia,
    metadata: skippedMetadata = {},
  } = skippedShotData;

  const { outputRanges } = skippedMetadata;

  content.contentById = {
    ...content.contentById,
    ...skippedContent.contentById,
  };
  content.content = [...content.content, ...skippedContent.content];
  elements.elementsById = {
    ...elements.elementsById,
    ...skippedElements.elementsById,
  };
  elements.elements = [...elements.elements, ...skippedElements.elements];
  elements.masks.root.frameEnd = lastShotEndFrame;
  media.mediaById = {
    ...media.mediaById,
    ...skippedMedia.mediaById,
  };
  media.media = [...media.media, ...skippedMedia.media];
  metadata.outputRanges = outputRanges;
  delete metadata.skippedShotData;

  return creativeData;
}

/*
 * update creative JSON with total number of source videos for looker
 */
export const addSourceMediaCount = (documentModel) => {
  const { elements, metadata } = documentModel;

  let totalSourceVideosCount = 0;
  elements.forEach((element) => {
    if (
      isNil(element.meta) ||
      element.meta.shotType !== SHOT_ELEMENT_TYPES.SOURCE
    ) {
      return;
    }
    totalSourceVideosCount++;
  });

  metadata.totalSourceVideosCount = totalSourceVideosCount;
  return documentModel;
};
